/**
 * FERP Portal - FHIR Labels Hook
 *
 * DRR-055: Backend-driven terminology via Setting config + ln-5-2-001 lens.
 *
 * Maps FHIR R4 resource type names to industry-specific display terms.
 * For ALF: "Patient" → "Resident", "Encounter" → "Service Visit", etc.
 *
 * The mapping comes from the `fhir_labels` section in the Setting config and is
 * evaluated through the `ln-5-2-001` lens (phase: setting). Backend surfaces it
 * via `settingBundle.fhirLabels`.
 *
 * Usage:
 * ```tsx
 * const { getFhirLabel } = useFhirLabels();
 * // In component: <h1>{getFhirLabel('Patient')}</h1>  → "Resident" (ALF)
 * ```
 */

import { useMemo } from 'react';
import { useGetSettingNavigationQuery } from '@/generated/graphql';

export interface UseFhirLabelsReturn {
  /** Map from FHIR resource type → industry display term */
  fhirLabels: Record<string, string>;
  /**
   * Get the industry display term for a FHIR resource type.
   * Falls back to the raw FHIR type name if no mapping exists.
   */
  getFhirLabel: (fhirResourceType: string) => string;
  loading: boolean;
}

export function useFhirLabels(): UseFhirLabelsReturn {
  const { data, loading } = useGetSettingNavigationQuery({
    fetchPolicy: 'cache-first',
  });

  const fhirLabels = useMemo<Record<string, string>>(() => {
    const raw = data?.settingBundle?.fhirLabels;
    if (!raw || typeof raw !== 'object') return {};
    return raw as Record<string, string>;
  }, [data?.settingBundle?.fhirLabels]);

  const getFhirLabel = useMemo(
    () =>
      (fhirResourceType: string): string =>
        fhirLabels[fhirResourceType] ?? fhirResourceType,
    [fhirLabels]
  );

  return { fhirLabels, getFhirLabel, loading };
}
